import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ExternalLink, Copy, CheckCircle2, ClipboardList, AlertTriangle, Sparkles, Network, ChevronDown, ChevronRight, Rocket, Bot, Zap } from "lucide-react";

const BLOCK_LABEL = {
  captcha_blocked: { text: "CAPTCHA required", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  no_fields_matched: { text: "Multi-step / SSO form", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  no_submit_button: { text: "No submit button found", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  load_failed: { text: "Signup page failed to load", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  submit_failed: { text: "Submit click failed", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  submitted_unconfirmed: { text: "Submitted but unconfirmed — verify", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  error: { text: "Bot crashed on this page", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

// Ordered so the biggest-payoff networks show up first
const NETWORK_META = {
  impact: {
    label: "Impact.com",
    signup_url: "https://app.impact.com/campaign-promo-signup/PublisherRegistration.brand",
    blurb: "Register once, then apply to every Impact-hosted brand in one click from their marketplace. Approvals are usually fast.",
    color: "bg-indigo-600",
  },
  partnerstack: {
    label: "PartnerStack",
    signup_url: "https://network.partnerstack.com/signup",
    blurb: "B2B SaaS-focused network. One profile, then apply to Notion, monday, ClickUp, etc. Some programs approve instantly.",
    color: "bg-fuchsia-600",
  },
  cj: {
    label: "CJ Affiliate",
    signup_url: "https://signup.cj.com/member/signup/publisher/",
    blurb: "Free, but CJ manually reviews your website — have real content on socialaffiliates.io before applying. Approves in 1–3 days.",
    color: "bg-amber-600",
  },
  shareasale: {
    label: "ShareASale",
    signup_url: "https://account.shareasale.com/newsignup.cfm",
    blurb: "Free US network. Review takes up to 3 business days, sometimes 20. Unlocks thousands of merchants.",
    color: "bg-emerald-600",
  },
  awin: {
    label: "Awin",
    signup_url: "https://www.awin.com/us/publishers",
    blurb: "Small refundable $1 deposit. Manual ID + content check — needs a working site.",
    color: "bg-cyan-600",
  },
  rakuten: {
    label: "Rakuten Advertising",
    signup_url: "https://rakutenadvertising.com/join/",
    blurb: "Free network. Household consumer brands. Approval is manual.",
    color: "bg-rose-600",
  },
  clickbank: {
    label: "ClickBank",
    signup_url: "https://accounts.clickbank.com/signup/",
    blurb: "Instant-approval marketplace of digital products. Sign up, browse, promote.",
    color: "bg-orange-600",
  },
  flexoffers: {
    label: "FlexOffers",
    signup_url: "https://publisher.flexoffers.com/Registration/PublisherSignup",
    blurb: "Free network aggregator. Manual review 1–3 days.",
    color: "bg-teal-600",
  },
  "in-house": {
    label: "In-house / direct",
    signup_url: null,
    blurb: "Each brand runs its own affiliate program directly — no network shortcut. Use the per-row signup links below.",
    color: "bg-slate-600",
  },
  other: {
    label: "Other / unknown",
    signup_url: null,
    blurb: "Network wasn't detected. Use the per-row signup link.",
    color: "bg-slate-500",
  },
};

const NETWORK_ORDER = ["impact", "partnerstack", "cj", "shareasale", "awin", "rakuten", "flexoffers", "clickbank", "in-house", "other"];

const NETWORK_PATTERNS = [
  ["impact", /\bimpact(\.com)?\b/i],
  ["partnerstack", /\bpartner\s?stack\b/i],
  ["cj", /\b(cj|commission\s?junction)\b/i],
  ["shareasale", /share\s?a\s?sale/i],
  ["awin", /\bawin\b/i],
  ["rakuten", /\brakuten\b/i],
  ["clickbank", /\bclick\s?bank\b/i],
  ["flexoffers", /\bflex\s?offers\b/i],
  ["in-house", /(in.?house|direct|proprietary|own\s+program)/i],
];

function classifyNetwork(guess) {
  const g = (guess || "").toLowerCase();
  for (const [key, re] of NETWORK_PATTERNS) if (re.test(g)) return key;
  return "other";
}

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text || "");
    toast.success("Copied to clipboard");
  } catch {
    toast.error("Copy failed — please copy manually");
  }
};

const LS_KEY = "sf_network_signup_checklist_v1";

export function AdminManualSprint({ client, API }) {
  const [rows, setRows] = useState([]);
  const [sprint, setSprint] = useState({ week: 0, total_done: 0, remaining: 0, total_packed: 0 });
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [openGroups, setOpenGroups] = useState({});
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState({});
  const [networkDone, setNetworkDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { return {}; }
  });
  const [autopilot, setAutopilot] = useState(null);
  const [copilot, setCopilot] = useState({});
  const [lightbox, setLightbox] = useState(null);
  const autopilotTimer = useRef(null);
  const copilotTimers = useRef({});

  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify(networkDone)); }, [networkDone]);

  const load = useCallback(() => {
    setLoading(true);
    client().get(`${API}/admin/official-links/manual-queue`)
      .then((r) => {
        setRows(r.data.programs || []);
        setSprint(r.data.sprint || { week: 0, total_done: 0, remaining: 0, total_packed: 0 });
      })
      .catch(() => toast.error("Failed to load manual queue"))
      .finally(() => setLoading(false));
  }, [client, API]);

  useEffect(() => { load(); }, [load]);

  // ---- Auto-Pilot: bot runs the whole pending queue, auto-applies the wins ----
  const pollAutopilot = useCallback(() => {
    client().get(`${API}/admin/official-links/auto-pilot/status`)
      .then((r) => {
        setAutopilot(r.data);
        if (r.data.running) {
          autopilotTimer.current = setTimeout(pollAutopilot, 3000);
        } else if (r.data.total > 0) {
          const done = (r.data.completed || []).length;
          const blocked = (r.data.blocked || []).length;
          toast.success(`Auto-Pilot done · ${done} auto-applied, ${blocked} need you`);
          load();
        }
      })
      .catch(() => { autopilotTimer.current = setTimeout(pollAutopilot, 4000); });
  }, [client, API, load]);

  const runAutoPilot = async () => {
    try {
      const { data } = await client().post(`${API}/admin/official-links/auto-pilot`);
      if (data.status === "nothing_pending") { toast.info("Nothing pending to auto-apply right now."); return; }
      toast.success(`Auto-Pilot started on ${data.count} programs — it'll auto-apply the ones without a CAPTCHA.`);
      setAutopilot({ running: true, total: data.count, processed: 0, completed: [], blocked: [] });
      pollAutopilot();
    } catch (e) {
      if (e?.response?.status === 409) { toast.info("Auto-Pilot is already running."); pollAutopilot(); }
      else toast.error(e?.response?.data?.detail || "Couldn't start Auto-Pilot");
    }
  };

  // Resume polling if a run is already in progress when the tab mounts
  useEffect(() => {
    client().get(`${API}/admin/official-links/auto-pilot/status`)
      .then((r) => { if (r.data.running) { setAutopilot(r.data); pollAutopilot(); } })
      .catch(() => {});
    return () => {
      clearTimeout(autopilotTimer.current);
      Object.values(copilotTimers.current).forEach(clearTimeout);
      Object.values(copilot).forEach((c) => c?.screenshotUrl && URL.revokeObjectURL(c.screenshotUrl));
    };
  }, []);

  // ---- Co-Pilot: bot fills a single form, screenshots where it stopped ----
  const pollCopilot = useCallback((slug, name) => {
    client().get(`${API}/admin/official-links/${slug}/submission-status`)
      .then(async (r) => {
        if (r.data.running) {
          copilotTimers.current[slug] = setTimeout(() => pollCopilot(slug, name), 2500);
          return;
        }
        const res = r.data.result || r.data.record?.submission_result || {};
        const st = res.status;
        if (st === "submitted" || st === "submitted_unconfirmed") {
          toast.success(`${name}: bot submitted it for you ✓`);
          setCopilot((c) => { const n = { ...c }; delete n[slug]; return n; });
          setRows((rs) => rs.filter((x) => x.slug !== slug));
          setSprint((s) => ({ ...s, week: s.week + 1, total_done: s.total_done + 1, remaining: Math.max(0, s.remaining - 1) }));
          return;
        }
        let url = "";
        try {
          const img = await client().get(`${API}/admin/official-links/${slug}/submission-screenshot`, { responseType: "blob" });
          url = URL.createObjectURL(img.data);
        } catch (err) { console.error("Screenshot fetch failed:", err?.response?.status || err?.message); }
        setCopilot((c) => ({ ...c, [slug]: { running: false, result: res, screenshotUrl: url } }));
        setExpanded((e) => ({ ...e, [slug]: true }));
        toast.message(`${name}: bot got stuck — take over below (answers are ready to paste)`);
      })
      .catch(() => { copilotTimers.current[slug] = setTimeout(() => pollCopilot(slug, name), 3000); });
  }, [client, API]);

  const botTriesFirst = async (slug, name) => {
    setCopilot((c) => ({ ...c, [slug]: { running: true } }));
    try {
      await client().post(`${API}/admin/official-links/${slug}/auto-submit`);
      pollCopilot(slug, name);
    } catch (e) {
      if (e?.response?.status === 409) { pollCopilot(slug, name); return; }
      toast.error(e?.response?.data?.detail || "Bot couldn't start on this one");
      setCopilot((c) => { const n = { ...c }; delete n[slug]; return n; });
    }
  };

  const markApplied = async (slug, name) => {
    if (!window.confirm(`Mark "${name}" as APPLIED?\n\nOnly click after you've submitted the application on their site.`)) return;
    setMarking(slug);
    try {
      await client().post(`${API}/admin/official-links/${slug}/mark-applied`);
      toast.success(`${name} marked applied ✓`);
      setRows((rs) => rs.filter((r) => r.slug !== slug));
      setSprint((s) => ({ ...s, week: s.week + 1, total_done: s.total_done + 1, remaining: Math.max(0, s.remaining - 1) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark applied");
    } finally {
      setMarking(null);
    }
  };

  const copyAll = async (row) => {
    const text = (row.answers || []).map((a) => `${a.field}:\n${a.value}\n`).join("\n");
    await copyText(text);
  };

  const openSignup = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyField = async (slug, idx, value) => {
    await copyText(value);
    setCopied((c) => ({ ...c, [`${slug}-${idx}`]: true }));
    setTimeout(() => setCopied((c) => ({ ...c, [`${slug}-${idx}`]: false })), 1500);
  };

  const toggleGroup = (key) => setOpenGroups((g) => ({ ...g, [key]: !(g[key] ?? true) }));
  const toggleNetworkDone = (key) => setNetworkDone((d) => ({ ...d, [key]: !d[key] }));

  // Group rows by detected network
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const g = {};
    for (const row of rows) {
      if (q && !`${row.program_name} ${row.niche_name} ${row.network_guess}`.toLowerCase().includes(q)) continue;
      const key = classifyNetwork(row.network_guess);
      if (!g[key]) g[key] = [];
      g[key].push(row);
    }
    return g;
  }, [rows, search]);

  const totalFiltered = Object.values(grouped).reduce((n, arr) => n + arr.length, 0);
  const networkGroupsPresent = NETWORK_ORDER.filter((k) => k !== "in-house" && k !== "other" && (grouped[k] || []).length > 0);

  return (
    <div data-testid="admin-manual-sprint" className="space-y-6">
      <div className="bg-gradient-to-br from-indigo-600 to-emerald-600 text-white rounded-lg p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-2 opacity-90">
              <ClipboardList className="w-4 h-4" /> Manual Apply Sprint
            </div>
            <h2 className="font-heading text-3xl tracking-tight">Finish the {rows.length} the bot couldn&apos;t</h2>
            <p className="text-sm text-white/80 max-w-2xl mt-2">
              Grouped by network so you sign up <b>once per network</b> and unlock the whole cluster in a few clicks.
            </p>
          </div>
          <div className="flex flex-col gap-2 items-stretch min-w-[200px]">
            <div data-testid="ms-streak" className="bg-white/15 border border-white/25 rounded-lg px-4 py-3 backdrop-blur">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold opacity-90">
                <span aria-hidden>🔥</span> This week
              </div>
              <div className="text-3xl font-bold leading-none mt-1">
                {sprint.week} <span className="text-base font-normal opacity-70">of {sprint.total_packed || rows.length + sprint.total_done} done</span>
              </div>
              {sprint.total_packed > 0 && (
                <div className="w-full h-1.5 bg-white/20 rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-white transition-all duration-500"
                    style={{ width: `${Math.min(100, Math.round((sprint.total_done / sprint.total_packed) * 100))}%` }} />
                </div>
              )}
              <p className="text-[10px] mt-1 opacity-80">{sprint.total_done} lifetime · {sprint.remaining} remaining</p>
            </div>
            <button onClick={runAutoPilot} disabled={autopilot?.running} data-testid="ms-autopilot"
              className="inline-flex items-center justify-center gap-1.5 text-xs font-bold px-4 py-2.5 rounded-full bg-white text-indigo-700 hover:bg-white/90 transition-colors disabled:opacity-60 shadow-sm">
              {autopilot?.running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {autopilot?.running ? "Auto-Pilot running…" : "🤖 Auto-Pilot all"}
            </button>
            <button onClick={load} data-testid="ms-refresh"
              className="text-xs font-semibold px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 transition-colors">
              Refresh queue
            </button>
          </div>
        </div>
      </div>

      {autopilot && autopilot.total > 0 && (
        <div data-testid="ms-autopilot-banner" className={`rounded-lg p-5 border ${autopilot.running ? "bg-indigo-50 border-indigo-200" : "bg-emerald-50 border-emerald-200"}`}>
          <div className="flex items-center gap-2 mb-2">
            {autopilot.running ? <Loader2 className="w-4 h-4 text-indigo-600 animate-spin" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
            <h3 className="font-heading text-lg">
              {autopilot.running
                ? `Auto-Pilot working… ${autopilot.processed || 0} of ${autopilot.total} checked`
                : `Auto-Pilot finished — ${(autopilot.completed || []).length} auto-applied, ${(autopilot.blocked || []).length} need you`}
            </h3>
          </div>
          <div className="w-full h-2 bg-white rounded-full overflow-hidden border border-slate-200">
            <div className="h-full bg-indigo-500 transition-all duration-500"
              style={{ width: `${Math.min(100, Math.round(((autopilot.processed || 0) / (autopilot.total || 1)) * 100))}%` }} />
          </div>
          {(autopilot.completed || []).length > 0 && (
            <p className="text-xs text-emerald-800 mt-3">
              ✅ Bot applied automatically: {autopilot.completed.map((c) => c.program_name).join(", ")}
            </p>
          )}
          {!autopilot.running && (autopilot.blocked || []).length > 0 && (
            <p className="text-xs text-slate-600 mt-1">
              🔒 Still need you (CAPTCHA / multi-step): {(autopilot.blocked || []).length} program{(autopilot.blocked || []).length !== 1 ? "s" : ""} below — use “Bot tries first” then take over.
            </p>
          )}
        </div>
      )}
      {networkGroupsPresent.length > 0 && (
        <div data-testid="ms-network-checklist" className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <Rocket className="w-4 h-4 text-indigo-600" />
            <h3 className="font-heading text-lg">Step 1 · Sign up to the {networkGroupsPresent.length} networks first</h3>
            <span className="ml-auto text-xs text-slate-500">
              {Object.values(networkDone).filter(Boolean).length} of {networkGroupsPresent.length} done
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {networkGroupsPresent.map((key) => {
              const meta = NETWORK_META[key];
              const count = grouped[key].length;
              const done = !!networkDone[key];
              return (
                <label key={key} data-testid={`ms-network-check-${key}`}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${done ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200 hover:border-slate-300"}`}>
                  <input type="checkbox" checked={done} onChange={() => toggleNetworkDone(key)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block w-2 h-2 rounded-full ${meta.color}`} />
                      <span className={`font-semibold text-sm ${done ? "line-through text-slate-400" : "text-slate-800"}`}>{meta.label}</span>
                      <span className="text-xs text-slate-500">unlocks {count}</span>
                    </div>
                    {meta.signup_url && (
                      <button onClick={(e) => { e.preventDefault(); openSignup(meta.signup_url); }}
                        className="inline-flex items-center gap-1 mt-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                        Open signup <ExternalLink className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Tip: get the biggest network (usually Impact) approved first — that unlocks the most programs and lets you rip through the network cluster below in one sitting. Progress is saved in this browser.
          </p>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search program, niche or network…" data-testid="ms-search"
          className="w-full px-4 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        <p className="text-xs text-slate-500 mt-2">Showing {totalFiltered} of {rows.length} program{rows.length !== 1 ? "s" : ""}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400" data-testid="ms-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading manual queue…
        </div>
      ) : totalFiltered === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-8 text-center" data-testid="ms-empty">
          <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
          <p className="font-heading text-xl">{rows.length === 0 ? "Sprint complete!" : "No programs match."}</p>
          <p className="text-sm text-emerald-800 mt-1">
            {rows.length === 0
              ? "Every packed program has been applied to. Approve the pending ones as they come back to unlock live traffic."
              : "Clear your search to see the queue."}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {NETWORK_ORDER.filter((k) => (grouped[k] || []).length > 0).map((netKey) => {
            const meta = NETWORK_META[netKey];
            const list = grouped[netKey];
            const isOpen = openGroups[netKey] ?? true;
            const netDone = !!networkDone[netKey];
            return (
              <section key={netKey} data-testid={`ms-group-${netKey}`} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <button onClick={() => toggleGroup(netKey)} data-testid={`ms-group-toggle-${netKey}`}
                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 transition-colors">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  <span className={`inline-flex items-center gap-1.5 text-white text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${meta.color}`}>
                    <Network className="w-3 h-3" /> {meta.label}
                  </span>
                  <span className="text-sm font-semibold text-slate-700">{list.length} program{list.length !== 1 ? "s" : ""}</span>
                  {netDone && <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">Network signup ✓</span>}
                  {meta.signup_url && !netDone && (
                    <button onClick={(e) => { e.stopPropagation(); openSignup(meta.signup_url); }}
                      data-testid={`ms-group-signup-${netKey}`}
                      className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-700 transition-colors">
                      <ExternalLink className="w-3.5 h-3.5" /> Sign up to {meta.label}
                    </button>
                  )}
                </button>
                {isOpen && (
                  <>
                    <div className="px-4 pb-3 -mt-1">
                      <p className="text-xs text-slate-500 max-w-3xl">{meta.blurb}</p>
                    </div>
                    <div className="border-t border-slate-100 divide-y divide-slate-100">
                      {list.map((row) => {
                        const block = BLOCK_LABEL[row.block_status] || null;
                        const isRowOpen = expanded[row.slug];
                        return (
                          <div key={row.slug} data-testid={`ms-row-${row.slug}`} className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-3 items-start">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <h3 className="font-semibold text-slate-900 truncate">{row.program_name}</h3>
                                  <span className="text-xs text-slate-400">· {row.niche_name}</span>
                                  {row.commission && <span className="text-xs text-emerald-700 font-semibold">· {row.commission}</span>}
                                </div>
                                {row.network_guess && (
                                  <p className="text-xs text-slate-500 mt-0.5">
                                    <Sparkles className="w-3 h-3 inline text-indigo-500 mr-1" />{row.network_guess}
                                  </p>
                                )}
                                {block && (
                                  <span className={`inline-flex items-center gap-1 mt-2 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${block.cls}`}>
                                    <AlertTriangle className="w-3 h-3" /> {block.text}
                                    {row.fields_filled_count > 0 && <span className="opacity-70">· bot filled {row.fields_filled_count}</span>}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <button onClick={() => botTriesFirst(row.slug, row.program_name)}
                                  disabled={copilot[row.slug]?.running}
                                  data-testid={`ms-bot-${row.slug}`}
                                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-indigo-600 text-white hover:bg-indigo-500 transition-colors disabled:opacity-60">
                                  {copilot[row.slug]?.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bot className="w-3.5 h-3.5" />}
                                  {copilot[row.slug]?.running ? "Bot working…" : "Bot tries first"}
                                </button>
                                <button onClick={() => openSignup(row.signup_url)} disabled={!row.signup_url}
                                  data-testid={`ms-open-${row.slug}`}
                                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-700 transition-colors disabled:opacity-40">
                                  <ExternalLink className="w-3.5 h-3.5" /> Open signup
                                </button>
                                <button onClick={() => setExpanded((e) => ({ ...e, [row.slug]: !e[row.slug] }))}
                                  data-testid={`ms-toggle-${row.slug}`}
                                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors">
                                  {isRowOpen ? "Hide answers" : `Show answers (${(row.answers || []).length})`}
                                </button>
                                <button onClick={() => copyAll(row)} data-testid={`ms-copyall-${row.slug}`}
                                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 transition-colors">
                                  <Copy className="w-3.5 h-3.5" /> Copy all
                                </button>
                                <button onClick={() => markApplied(row.slug, row.program_name)}
                                  disabled={marking === row.slug}
                                  data-testid={`ms-mark-${row.slug}`}
                                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-500 transition-colors disabled:opacity-40">
                                  {marking === row.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Mark applied
                                </button>
                              </div>
                            </div>

                            {isRowOpen && (
                              <div className="mt-4 bg-slate-50/60 border border-slate-200 rounded-lg p-4">
                                {row.tips?.length > 0 && (
                                  <div className="mb-4 bg-white border border-indigo-100 rounded-lg p-3">
                                    <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 mb-1.5">Approval tips</p>
                                    <ul className="text-xs text-slate-700 space-y-1 list-disc list-inside">
                                      {row.tips.map((t) => <li key={t}>{t}</li>)}
                                    </ul>
                                  </div>
                                )}
                                <div className="space-y-2" data-testid={`ms-answers-${row.slug}`}>
                                  {(row.answers || []).map((a, idx) => (
                                    <div key={`${a.field}-${idx}`} className="bg-white border border-slate-200 rounded-lg p-3 flex items-start gap-3">
                                      <div className="flex-1 min-w-0">
                                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{a.field}</p>
                                        <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap break-words">{a.value}</p>
                                      </div>
                                      <button onClick={() => copyField(row.slug, idx, a.value)}
                                        data-testid={`ms-copy-${row.slug}-${idx}`}
                                        className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-700 transition-colors">
                                        {copied[`${row.slug}-${idx}`] ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                        {copied[`${row.slug}-${idx}`] ? "Copied" : "Copy"}
                                      </button>
                                    </div>
                                  ))}
                                  {(!row.answers || row.answers.length === 0) && (
                                    <p className="text-xs text-slate-400 italic">No AI answers cached. Generate a pack in Link Manager first.</p>
                                  )}
                                </div>
                              </div>
                            )}
                            {copilot[row.slug] && !copilot[row.slug].running && copilot[row.slug].result && (
                              <div data-testid={`ms-copilot-${row.slug}`} className="mt-4 bg-indigo-50/70 border border-indigo-200 rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <Bot className="w-4 h-4 text-indigo-600" />
                                  <p className="text-sm font-semibold text-indigo-900">Bot got as far as it could — your turn</p>
                                  {copilot[row.slug].result.captcha_detected && (
                                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">CAPTCHA — needs a human</span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-600 mb-3">
                                  {copilot[row.slug].result.reason || "The bot stopped here."}
                                  {copilot[row.slug].result.fields_filled_count > 0 && ` · It filled ${copilot[row.slug].result.fields_filled_count} field(s) for you.`}
                                </p>
                                {copilot[row.slug].screenshotUrl && (
                                  <div className="relative mb-3 group">
                                    <img src={copilot[row.slug].screenshotUrl} alt="Bot screenshot"
                                      onClick={() => setLightbox(copilot[row.slug].screenshotUrl)}
                                      data-testid={`ms-copilot-shot-${row.slug}`}
                                      className="w-full rounded-md border border-slate-200 max-h-80 object-cover object-top cursor-zoom-in" />
                                    <span className="absolute top-2 right-2 text-[10px] font-semibold bg-black/60 text-white px-2 py-0.5 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                                      Click to zoom
                                    </span>
                                  </div>
                                )}
                                <div className="flex items-center gap-2 flex-wrap">
                                  <button onClick={() => openSignup(row.signup_url)}
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-indigo-600 text-white hover:bg-indigo-500 transition-colors">
                                    <ExternalLink className="w-3.5 h-3.5" /> Take over → open signup
                                  </button>
                                  <button onClick={() => copyAll(row)}
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 transition-colors">
                                    <Copy className="w-3.5 h-3.5" /> Copy all answers
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </section>
            );
          })}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightbox(null)} data-testid="ms-lightbox">
          <button onClick={() => setLightbox(null)} data-testid="ms-lightbox-close"
            className="absolute top-4 right-6 text-white/80 hover:text-white text-4xl leading-none">×</button>
          <img src={lightbox} alt="Bot screenshot full size"
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[92vh] rounded-lg shadow-2xl object-contain cursor-default" />
        </div>
      )}
    </div>
  );
}
